import { useLayoutEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input.jsx';
import { cn } from '@/lib/utils.js';
import { fmtMoney, toNumber, normalizeMultiline, parseTermAmount } from '@/lib/quoteFields.js';

// web-v2/src/components/quote/QuoteControls.jsx
//
// The quote form's input primitives, LIFTED VERBATIM out of QuoteEntrySheet.jsx.
// QUOTE-WIZARD-SPEC phase 1 adds a second surface that asks the same questions in
// a different shape; these are the controls both surfaces use, so a money field
// behaves identically on either. No behaviour change from the originals.
//
// The pure helpers they lean on live in @/lib/quoteFields.js.

export const inputClass = cn(
    'flex min-h-[44px] w-full rounded-card border border-border bg-bg px-3 py-2 text-sm text-text',
    'placeholder:text-text-dim',
    'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1 focus:ring-offset-bg',
    'disabled:cursor-not-allowed disabled:opacity-50'
);

// Money input. Formats $/commas on blur, raw digits on focus so backspace works
// naturally. Stores digits-only internally and emits the raw numeric STRING via
// onChange, so toNumber() in every save path is unchanged.
export function MoneyInput({ value, onChange, placeholder, inputMode = 'decimal', className, autoFocus, ...rest }) {
    const [focused, setFocused] = useState(false);
    const digits = String(value ?? '').replace(/[^0-9.]/g, '');
    // Unfocused, the grouped number WITHOUT a currency sign: the prefix span is the one "$" (review 2026-11-03,
    // the box read "$ $3,000").
    const shownNum = toNumber(digits);
    const display = focused
        ? digits
        : (shownNum == null ? '' : shownNum.toLocaleString('en-US'));
    return (
        <div className="relative">
            <span
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-muted"
            >
                $
            </span>
            <Input
                {...rest}
                autoFocus={autoFocus}
                inputMode={inputMode}
                placeholder={placeholder}
                value={display}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9.]/g, '');
                    onChange(raw);
                }}
                className={cn('pl-7', className)}
            />
        </div>
    );
}

// `tag` is a short word beside the label, currently used for one thing: marking
// a value the form filled in rather than one the broker set (QUOTE-WIZARD item
// 5, 2026-07-30). It is a LABEL, not a sentence. Omitted everywhere else, so
// QuoteEntrySheet is unchanged.
// `hint` is a lowercase word UNDER the label, for the ones that would otherwise
// live inside it. "MEDICAL PAYMENTS PREMIUM (OPTIONAL)" wrapped badly and the
// parenthetical was competing with the field name in the same uppercase weight
// (prompt 104 C6). David: 'or move optional to subtext on all of them' — the
// second option, because it fixes all three premium fields rather than
// shortening one of them.
export function Field({ label, children, tag, hint }) {
    return (
        <label className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-text-muted">
                {label}
            </span>
            {tag ? (
                <span className="ml-1.5 rounded-pill border border-border bg-bg px-1.5 py-px text-[9.5px] font-medium uppercase tracking-wide text-text-dim">
                    {tag}
                </span>
            ) : null}
            {hint ? (
                <span className="ml-1.5 text-[10px] normal-case tracking-normal text-text-dim">{hint}</span>
            ) : null}
            <div className="mt-1.5">{children}</div>
        </label>
    );
}

// A textarea that fits what is in it (item 6b, 2026-07-30). David: "pasting
// more than what fits...can it expand to fit?" Every one of these was a fixed
// three rows with resize-none, so pasting a carrier's open pilot clause meant
// reading it through a peephole that could not even be dragged open.
//
// It grows with the content up to maxHeight and then scrolls, and it stays
// draggable. Input is normalised on the way in (normalizeMultiline), which is
// what actually fixes the pasted-carriage-return complaint: the doubled breaks
// disappear as he pastes rather than on the next reload.
//
// onChange emits the normalised STRING, matching MoneyInput, not an event.
export function AutoTextarea({ value, onChange, minRows = 2, maxHeight = 320, className, ...rest }) {
    const ref = useRef(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    }, [value, maxHeight]);
    return (
        <textarea
            {...rest}
            ref={ref}
            rows={minRows}
            value={value ?? ''}
            onChange={(e) => onChange(normalizeMultiline(e.target.value))}
            className={cn(inputClass, 'resize-y', className)}
        />
    );
}

// Quick-pick pills under a money input (moved here from QuoteWizardSheet so every sheet shares one). A pick only
// fills the box; the box takes any amount. `active` marks the pill equal to the current value.
export function QuickPicks({ values, onPick, active }) {
    return (
        <div className="mt-1 flex flex-wrap gap-1">
            {values.map((v) => {
                const on = active != null && Number(active) === v;
                return (
                    <button
                        key={v}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onPick(v)}
                        className={cn(
                            'rounded-pill border bg-bg px-2 py-0.5 text-[10px] hover:border-border-strong',
                            on ? 'border-accent text-text' : 'border-border text-text-muted'
                        )}
                    >
                        {fmtMoney(v)}
                    </button>
                );
            })}
        </div>
    );
}

// One typed money term (David 2026-11-03: "i need option to type in any number for all the terms. quick selects
// are fine"). The value is a whole-dollar NUMBER or null (not stated), the shape the coverage doors already send.
// Any amount is typed; quick picks fill it; Clear means not stated. $0 shows as $0 and blank stays blank
// (parseTermAmount owns the rule; tests/term-amount-field.test.js).
// While the box has focus it shows the TEXT being typed (draft), never the number redrawn from it: redrawing dropped
// a typed "." and the digits after it joined the dollars ("5000.00" typed key by key stored $500,000; review
// 2026-11-03). The parent is told parseTermAmount(text) on every key; on blur the box shows the stored number.
export function TermAmountField({ value, onChange, quick = [], placeholder = 'Not stated', ariaLabel, className }) {
    const stated = value != null && value !== '';
    const [draft, setDraft] = useState(null);
    const shown = draft != null ? draft : (stated ? Number(value).toLocaleString('en-US') : '');
    return (
        <div className={className}>
            <div className="relative">
                {shown !== '' || draft != null ? (
                    <span aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-muted">
                        $
                    </span>
                ) : null}
                <Input
                    aria-label={ariaLabel}
                    inputMode="decimal"
                    placeholder={placeholder}
                    value={shown}
                    onFocus={() => setDraft(stated ? String(value) : '')}
                    onBlur={() => setDraft(null)}
                    onChange={(e) => { setDraft(e.target.value); onChange(parseTermAmount(e.target.value)); }}
                    className="pl-7"
                />
            </div>
            <div className="flex flex-wrap items-start gap-1">
                {quick.length > 0 ? <QuickPicks values={quick} active={stated ? value : null} onPick={(v) => onChange(v)} /> : null}
                {stated ? (
                    <button
                        type="button"
                        onClick={() => onChange(null)}
                        className="mt-1 rounded-pill px-2 py-0.5 text-[10px] text-text-dim underline-offset-2 hover:underline"
                    >
                        Clear{ariaLabel ? ` ${ariaLabel.toLowerCase()}` : ''}
                    </button>
                ) : null}
            </div>
        </div>
    );
}

export function Helper({ children }) {
    return <span className="mt-1 block text-[10px] text-text-dim">{children}</span>;
}
